import { createMemo } from 'solid-js'
import { isSleepInProgress, type MeasurementEntry } from '@fasting-timer/shared'

import { translate } from '../i18n'
import { formatDateTimeLocalValue, parseDateTimeLocalValue } from '../utils/time'
import { createEditModalState } from './editModalState'
import {
   addMeasurement,
   deleteMeasurement,
   getEntryTimestampMs,
   liveMeasurementEntries,
   measurementEntries,
   setMeasurementStatusMessage,
   updateMeasurement,
} from './measurementsStore'

/** Hours of sleep the progress ring counts towards. */
export const SLEEP_TARGET_HOURS = 8
export const SLEEP_TARGET_MS = SLEEP_TARGET_HOURS * 60 * 60 * 1000

/**
 * Sleep is the `sleep` measurement: the entry's timestamp is when the sleep started,
 * and `duration` (seconds) is set when it ends. Alexa's "good night" and "good
 * morning" write the same entries on the server, and they arrive through measurement sync.
 */
const sleepEntries = createMemo(() =>
   liveMeasurementEntries().filter(entry => entry.metricId === 'sleep'),
)

/** The sleep that has started but not ended, if any. */
const sleepInProgress = createMemo(() => sleepEntries().find(isSleepInProgress) ?? null)

/** Ended sleeps, newest first. */
const completedSleeps = createMemo(() =>
   sleepEntries().filter(entry => !isSleepInProgress(entry)),
)

/**
 * Returns when a sleep ended, or null while it is still running.
 *
 * @param entry - The sleep entry.
 * @returns Epoch ms of the wake-up time, or null.
 */
export function getSleepEndMs(entry: MeasurementEntry): number | null {
   const duration = entry.values.duration
   return duration === undefined ? null : getEntryTimestampMs(entry) + duration * 1000
}

/**
 * Starts the sleep timer now, unless one is already running.
 */
export function startSleep() {
   if (sleepInProgress()) {
      return
   }
   addMeasurement('sleep', {})
   setMeasurementStatusMessage('')
}

/**
 * Converts a sleep length to the stored whole seconds (at least one, since
 * durations must be positive).
 *
 * @param startMs - When the sleep started.
 * @param endMs - When it ended.
 * @returns The duration in seconds.
 */
function toDurationSeconds(startMs: number, endMs: number) {
   return Math.max(1, Math.round((endMs - startMs) / 1000))
}

/**
 * Ends the running sleep now and stores its duration.
 */
export function endSleep() {
   const running = sleepInProgress()
   if (!running) {
      return
   }
   const startMs = getEntryTimestampMs(running)
   updateMeasurement(running.entryId, {
      values: { duration: toDurationSeconds(startMs, Math.max(startMs, Date.now())) },
   })
   setMeasurementStatusMessage('')
}

const sleepEdit = createEditModalState(['start', 'end'] as const)
let editingSleepId: string | null = null

export const isSleepEditModalOpen = sleepEdit.isOpen
export const editingSleepStart = sleepEdit.valueAccessor('start')
export const editingSleepEnd = sleepEdit.valueAccessor('end')
export const sleepEditError = sleepEdit.error
export const setEditingSleepStart = (value: string) => sleepEdit.setValue('start', value)
export const setEditingSleepEnd = (value: string) => sleepEdit.setValue('end', value)

/**
 * Opens the sleep edit modal with the entry's start and (if ended) wake-up time.
 *
 * @param entryId - The sleep entry to edit.
 */
export function openSleepEditModal(entryId: string) {
   const entry = measurementEntries().find(item => item.entryId === entryId)
   if (!entry || entry.deletedAt !== null) {
      return
   }
   editingSleepId = entryId
   const endMs = getSleepEndMs(entry)
   sleepEdit.open(0, {
      start: formatDateTimeLocalValue(getEntryTimestampMs(entry)),
      end: endMs === null ? '' : formatDateTimeLocalValue(endMs),
   })
}

/**
 * Closes the sleep edit modal.
 */
export function closeSleepEditModal() {
   editingSleepId = null
   sleepEdit.close()
}

/**
 * Deletes the sleep entry open in the edit modal and closes the modal.
 */
export function deleteEditedSleep() {
   const entryId = editingSleepId
   closeSleepEditModal()
   if (entryId !== null) {
      deleteMeasurement(entryId)
   }
}

/**
 * Validates the edited times and saves them. An empty wake-up time keeps (or makes)
 * the sleep running, which is allowed only when no other sleep is running.
 */
export function saveEditedSleep() {
   const entryId = editingSleepId
   if (!entryId) {
      return
   }
   const startMs = parseDateTimeLocalValue(editingSleepStart())
   if (startMs === null) {
      sleepEdit.setError(translate('Enter when the sleep started.'))
      return
   }
   const endInput = editingSleepEnd().trim()
   const endMs = endInput ? parseDateTimeLocalValue(endInput) : null
   if (endInput && endMs === null) {
      sleepEdit.setError(translate('Enter when you woke up.'))
      return
   }
   if (endMs !== null && endMs <= startMs) {
      sleepEdit.setError(translate('Waking up must come after falling asleep.'))
      return
   }
   const running = sleepInProgress()
   if (endMs === null && running && running.entryId !== entryId) {
      sleepEdit.setError(translate('Another sleep is already being timed.'))
      return
   }
   const error = updateMeasurement(entryId, {
      timestampMs: startMs,
      values: endMs === null ? {} : { duration: toDurationSeconds(startMs, endMs) },
   })
   if (error) {
      sleepEdit.setError(error)
      return
   }
   closeSleepEditModal()
}

export { completedSleeps, sleepEntries, sleepInProgress }
